import React, { useState, useEffect } from "react";
import { 
  Card, 
  Grid, 
  Typography, 
  Button, 
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Skeleton from "@material-ui/lab/Skeleton";
import SaveIcon from "@material-ui/icons/Save";
import WarningIcon from "@material-ui/icons/Warning";
import AssumptionsByType from "./AssumptionsByTypeTable";
import CoverageRatioTable from "./CoverageRatioTable";

const AssumptionsSection = ({ assumptionsData, loading, onSaveAssumptions }) => {
  // Local state for all editable data
  const [localLeadSourceData, setLocalLeadSourceData] = useState([]);
  const [localCustTypeData, setLocalCustTypeData] = useState([]);
  const [localCoverageRatioData, setLocalCoverageRatioData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Track original data for comparison
  const [originalLeadSourceData, setOriginalLeadSourceData] = useState([]);
  const [originalCustTypeData, setOriginalCustTypeData] = useState([]);
  const [originalCoverageRatioData, setOriginalCoverageRatioData] = useState([]);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Initialize local data when props change
  useEffect(() => {
    if (assumptionsData && assumptionsData !== "") {
      const leadSource = [...(assumptionsData.leadSourceData || [])];
      const custType = [...(assumptionsData.custTypeData || [])];
      const coverageRatio = [...(assumptionsData.coverageRatioData || [])];
      
      setLocalLeadSourceData(leadSource);
      setLocalCustTypeData(custType);
      setLocalCoverageRatioData(coverageRatio);
      
      // Store original data for comparison
      setOriginalLeadSourceData(JSON.parse(JSON.stringify(leadSource)));
      setOriginalCustTypeData(JSON.parse(JSON.stringify(custType)));
      setOriginalCoverageRatioData(JSON.parse(JSON.stringify(coverageRatio)));
      
      setHasChanges(false);
    }
  }, [assumptionsData]);

  // Handle changes from child components
  const handleLeadSourceChange = (updatedData) => {
    setLocalLeadSourceData(updatedData);
    setHasChanges(true);
  };

  const handleCustTypeChange = (updatedData) => {
    setLocalCustTypeData(updatedData);
    setHasChanges(true);
  };

  const handleCoverageRatioChange = (updatedData) => {
    setLocalCoverageRatioData(updatedData);
    setHasChanges(true);
  };

  // Get only changed cells
  const getChangedCells = () => {
    const changedLeadSource = [];
    const changedCustType = [];
    const changedCoverageRatio = [];

    // Compare Lead Source data
    localLeadSourceData.forEach((item, index) => {
      const original = originalLeadSourceData[index];
      if (original && item.Percentage !== original.Percentage) {
        changedLeadSource.push({
          Lead_Source: item.Lead_Source,
          Team: item.Team,
          Percentage: item.Percentage, // Convert back to decimal
        });
      }
    });

    // Compare Customer Type data
    localCustTypeData.forEach((item, index) => {
      const original = originalCustTypeData[index];
      if (original && item.Percentage !== original.Percentage) {
        changedCustType.push({
          Cust_Type: item.Cust_Type,
          Team: item.Team,
          Percentage: item.Percentage, // Convert back to decimal
        });
      }
    });

    // Compare Coverage Ratio data
    localCoverageRatioData.forEach((item, index) => {
      const original = originalCoverageRatioData[index];
      if (original && item.COVERAGE_RATIO !== original.COVERAGE_RATIO) {
        changedCoverageRatio.push({
          CURRENT_QTR_PLUS: item.CURRENT_QTR_PLUS,
          COVERAGE_RATIO: item.COVERAGE_RATIO,
        });
      }
    });

    return {
      leadSourceData: changedLeadSource.length > 0 ? changedLeadSource : undefined,
      custTypeData: changedCustType.length > 0 ? changedCustType : undefined,
      coverageRatioData: changedCoverageRatio.length > 0 ? changedCoverageRatio : undefined,
    };
  };

  // Validate all data and return validation errors
  const validateAllData = () => {
    const errors = [];

    // Validate Lead Source percentages
    const leadSourceTeamTotals = {};
    localLeadSourceData.forEach((item) => {
      if (!leadSourceTeamTotals[item.Team]) {
        leadSourceTeamTotals[item.Team] = 0;
      }
      leadSourceTeamTotals[item.Team] += item.Percentage;
    });

    Object.entries(leadSourceTeamTotals).forEach(([team, total]) => {
      if (Math.abs(total - 100.0) > 0.01) {
        errors.push({
          type: "Lead Source",
          team: team,
          total: total.toFixed(2),
          isExceeding: total > 100,
        });
      }
    });

    // Validate Customer Type percentages
    const custTypeTeamTotals = {};
    localCustTypeData.forEach((item) => {
      if (!custTypeTeamTotals[item.Team]) {
        custTypeTeamTotals[item.Team] = 0;
      }
      custTypeTeamTotals[item.Team] += item.Percentage;
    });

    Object.entries(custTypeTeamTotals).forEach(([team, total]) => {
      if (Math.abs(total - 100.0) > 0.01) {
        errors.push({
          type: "Customer Type",
          team: team,
          total: total.toFixed(2),
          isExceeding: total > 100,
        });
      }
    });

    // Validate Coverage Ratios are positive
    localCoverageRatioData.forEach((item) => {
      if (item.COVERAGE_RATIO < 0) {
        errors.push({
          type: "Coverage Ratio",
          team: item.CURRENT_QTR_PLUS,
          message: "Coverage ratio must be a positive number",
        });
      }
    });

    return errors;
  };

  // Check if submit should be disabled
  const isSubmitDisabled = () => {
    if (!hasChanges) return true;
    
    // const errors = validateAllData();
    // // Disable if any team exceeds 100%
    // return errors.some((error) => error.isExceeding);
  };

  // Handle submit button click - open modal
  const handleSubmitClick = () => {
    const errors = validateAllData();
    setValidationErrors(errors);
    setOpenModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Handle confirm submit
  const handleConfirmSubmit = () => {
    setOpenModal(false);

    // Get only changed cells
    const changedData = getChangedCells();
    
    // Build payload with only changed data
    const payload = {};
    if (changedData.leadSourceData) {
      payload.leadSourceData = changedData.leadSourceData;
    }
    if (changedData.custTypeData) {
      payload.custTypeData = changedData.custTypeData;
    }
    if (changedData.coverageRatioData) {
      payload.coverageRatioData = changedData.coverageRatioData;
    }

    // Only send if there are changes
    if (Object.keys(payload).length === 0) {
      setSnackbar({
        open: true,
        message: "No changes to save",
        severity: "info",
      });
      return;
    }

    console.log("Sending only changed cells:", payload);

    // Call parent save function
    if (onSaveAssumptions) {
      onSaveAssumptions(payload);
      setHasChanges(false);
      setSnackbar({
        open: true,
        message: "Saving changes...",
        severity: "info",
      });
    }
  };

  // Old handleSubmitAll replaced by handleSubmitClick and handleConfirmSubmit

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading || assumptionsData === "") {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} key={item} style={{ padding: "1rem", paddingLeft: 0, paddingTop: '2rem' }}>
            <Grid item container component={Card} justify="center">
              <Skeleton
                animation="wave"
                variant="rect"
                width="80%"
                height={200}
                style={{ margin: "20px 0px" }}
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      {/* Submit Button - Fixed at top */}


      {/* Lead Source Section */}
      <Grid item xs={12} style={{ padding: "1rem", paddingLeft: 0 }}>
        <Grid item container xs={12} justify="flex-start">
          <Card style={{ padding: "1rem 2rem", width: "100%" }}>
            <Grid item container xs={12} justify="center">
              <Grid item xs={1}></Grid>
              <Grid item xs={10}>
                <Typography
                  variant="h2"
                  style={{
                    width: "100%",
                    margin: "auto",
                    padding: "1rem",
                    paddingTop: "0.3rem",
                  }}
                  align="center"
                >
                  Pipeline Goal By Lead Source
                </Typography>
              </Grid>
              <Grid item xs={1} align="right"></Grid>
            </Grid>
            <AssumptionsByType
              data={localLeadSourceData}
              data-test="Assumptions-PGSA-Pipeline-Lead-Source"
              type="Lead_Source"
              firstColumnHeader="Lead Source"
              onChange={handleLeadSourceChange}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Customer Type Section */}
      <Grid item xs={12} style={{ padding: "1rem", paddingLeft: 0 }}>
        <Grid item container xs={12} justify="flex-start">
          <Card style={{ padding: "1rem 2rem", width: "100%" }}>
            <Grid item container xs={12} justify="center">
              <Grid item xs={1}></Grid>
              <Grid item xs={10}>
                <Typography
                  variant="h2"
                  style={{
                    width: "100%",
                    margin: "auto",
                    padding: "1rem",
                    paddingTop: "0.3rem",
                  }}
                  align="center"
                >
                  Pipeline Goal By Customer Type
                </Typography>
              </Grid>
              <Grid item xs={1} align="right"></Grid>
            </Grid>
            <AssumptionsByType
              data={localCustTypeData}
              data-test="Assumptions-PGSA-Pipeline-Cust-Type"
              type="Cust_Type"
              firstColumnHeader="Customer Type"
              onChange={handleCustTypeChange}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Coverage Ratio Section */}
      <Grid item xs={12} style={{ padding: "1rem", paddingLeft: 0 }}>
        <Grid item container xs={12} justify="flex-start">
          <Card style={{ padding: "1rem 2rem", width: "100%" }}>
            <Grid item container xs={12} justify="center">
              <Grid item xs={1}></Grid>
              <Grid item xs={10}>
                <Typography
                  variant="h2"
                  style={{
                    width: "100%",
                    margin: "auto",
                    padding: "1rem",
                    paddingTop: "0.3rem",
                  }}
                  align="center"
                >
                  Coverage Ratio Goal for Future Quarters
                </Typography>
              </Grid>
              <Grid item xs={1} align="right"></Grid>
            </Grid>
            <CoverageRatioTable
              data={localCoverageRatioData}
              data-test="Assumptions-PGSA-Coverage-Ratio"
              onChange={handleCoverageRatioChange}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Submit Button - Also at bottom */}
    
        <Grid item xs={12} style={{ padding: "1rem 0", textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmitClick}
            disabled={isSubmitDisabled()}
            // startIcon={<SaveIcon />}
            color="primary"
          >
            Update
          </Button>
        </Grid>
      


      {/* Confirmation Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="confirmation-dialog-title"
      >
        <DialogTitle id="confirmation-dialog-title">
          {validationErrors.length === 0 ? "Confirm Submission" : "Validation Error"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {validationErrors.length === 0 ? (
              "Are you sure you want to save the changes?"
            ) : (
              <div>
                <Typography variant="body1" style={{ marginBottom: "10px", fontWeight: 600 }}>
                  Please review the following issues:
                </Typography>
                {validationErrors.map((error, index) => (
                  <div key={index} style={{ marginBottom: "8px" }}>
                    {error.isExceeding ? (
                      <Typography style={{ color: "#d32f2f" }}>
                        <WarningIcon style={{ verticalAlign: "middle", marginRight: "8px" }} />
                        <strong>{error.type}</strong> for team <strong>{error.team}</strong> is exceeding 100% (Total: {error.total}%)
                      </Typography>
                    ) : (
                      <Typography style={{ color: "#f57c00" }}>
                        ⚠️ <strong>{error.type}</strong> for team <strong>{error.team}</strong> does not sum to 100% (Total: {error.total}%)
                      </Typography>
                    )}
                  </div>
                ))}
                <Typography variant="body2" style={{ marginTop: "16px", color: "#d32f2f", fontWeight: 600 }}>
                  Please fix the values so that each team sums to exactly 100%.
                </Typography>
              </div>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {validationErrors.length === 0 ? (
            // Show Yes/No when no validation errors
            <>
              <Button onClick={handleCloseModal} color="primary" >
                No
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                color="primary"
                variant="contained"
                // style={{ backgroundColor: "#4caf50" }}
              >
                Yes
              </Button>
            </>
          ) : (
            // Show only OK when validation errors exist
            <Button 
              onClick={handleCloseModal} 
              color="primary"
              variant="contained"
            >
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
    
    </>
  );
};

export default AssumptionsSection;